import React from 'react';
import { CloseOutlined } from '@ant-design/icons';
import type { VinInspectionItem } from '../inspectionTypes';
import ContentLayout from '../../../components/ContentLayout';
import RightDrawer from '../../../components/Drawers/RightDrawer';
import VinDetails from '../../../pages/VinDetails/VinDetails';
import { CountIndicator } from '../../../components/CellRenderers/CountCellRenderer';
import { getInspectionSelection } from '../inspectionUtils';

interface InspectionInfoDrawerProps {
  inspection: VinInspectionItem | null;
  open: boolean;
  onClose: () => void;
  size?: number;
}

const InspectionInfoDrawer: React.FC<InspectionInfoDrawerProps> = ({ inspection, open, onClose, size = 700 }) => {
  const { inspectionId, inspectionDateTime } = getInspectionSelection(inspection);

  // RightDrawer keeps VinDetails mounted across opens/closes, so its expanded-panel
  // state can carry over from a previous inspection. Bump a nonce whenever the drawer
  // opens or a different row is selected so VinDetails remounts fresh and re-applies
  // the panel for the newly selected inspection instead of reusing stale state.
  const selectionKey = `${inspection?.vin ?? ''}|${inspectionId ?? ''}|${inspectionDateTime ?? ''}`;
  const [openNonce, setOpenNonce] = React.useState(0);
  const wasOpenRef = React.useRef(open);
  const lastSelectionKeyRef = React.useRef(selectionKey);
  if ((open && !wasOpenRef.current) || (open && selectionKey !== lastSelectionKeyRef.current)) {
    setOpenNonce(nonce => nonce + 1);
  }
  wasOpenRef.current = open;
  lastSelectionKeyRef.current = selectionKey;

  return (
    <RightDrawer
      size={size}
      closable={false}
      mask={false}
      title={
        <div className="flex items-center justify-between w-full">
          <span className="flex text-sm">
            <div className="me-2">
              <CountIndicator value={(inspection?.inspectionCount as number) || 1} size={30} />
            </div>
            <div className="text-gray-750 font-bold me-2">{inspection?.vin || 'N/A'}</div>
            <div className="ms-2 text-sm text-gray-600">
              | {inspection?.make || 'N/A'} | {inspection?.model || 'N/A'}
            </div>
          </span>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-full bg-black/60 border border-white/30 flex items-center justify-center cursor-pointer z-10"
            aria-label="Close drawer">
            <CloseOutlined className="text-white text-sm" />
          </button>
        </div>
      }
      open={open}
      onClose={onClose}
      rootClassName="inspection-info-drawer">
      <ContentLayout compact>
        <VinDetails
          key={openNonce}
          vin={inspection?.vin || ''}
          mode="drawer"
          visibleTabs={['defectImages']}
          readOnlyDamage
          initialInspectionId={inspectionId}
          initialInspectionDateTime={inspectionDateTime}
        />
      </ContentLayout>
    </RightDrawer>
  );
};

export default InspectionInfoDrawer;
